import { useEffect, useRef, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { Button, ContactMessage, LoadingSpinner, InlineAlert } from '../components/BaseComponents'
import CsvResult from '../components/BaseComponents/CsvResult/CsvResult'
import PageHeadings from '../components/BaseComponents/PageHeadings/PageHeadings'
import PageTemplate from '../components/BaseComponents/PageTemplate/PageTemplate'
import routes from '../components/Router/routes'
import { addPurchasesUploadFile } from '../redux/actions/addPurchasesUploadActions'
import { useAppDispatch, useAppSelector } from '../redux/hooks/reduxHooks'
import {
	selectAddPurchasesAccountingId,
	selectAddPurchasesFileAccountingPeriod,
	selectAddPurchasesFileErrorCount,
	selectAddPurchasesFileErrors,
	selectAddPurchasesFileRecords,
	selectAddPurchasesUploadError,
	selectAddPurchasesUploadRetrySafe,
	selectAddPurchasesUploadFile,
	selectAddPurchasesUploadId,
	selectAddPurchasesUploadName,
	selectAddPurchasesUploadStatus,
	selectAddPurchasesUploadSuccessful,
	ADDPURCHASESUPLOAD_FAILURE,
	ADDPURCHASESUPLOAD_RESET,
	ADDPURCHASESUPLOAD_UNCERTAIN,
} from '../redux/slices/addPurchasesUploadSlice'
import { PostStatus } from '../types/LoadingStates'
import { ACCOUNTINGPERIODS_UPDATE_RESET, selectAccountingPeriodsUpdateError, selectAccountingPeriodsUpdateStatus } from '../redux/slices/accountingPeriodsSlice'
import { PurchaseUploadState } from '../enums/PurchaseUploadState'
import useApi from '../hooks/useApi'
import useAddNotification from '../hooks/useAddNotification'
import useCleanup from '../hooks/useCleanup'
import useCompleteAccountingStage from '../hooks/useCompleteAccountingStage'

const AddPurchasesUpload = () => {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const api = useApi();
	const notify = useAddNotification()

	const fileToUpload = useAppSelector(selectAddPurchasesUploadFile)
	const fileToUploadAccountingPeriodId = useAppSelector(selectAddPurchasesUploadId)

	const fileToUploadAccountingPeriodName = useAppSelector(selectAddPurchasesUploadName)

	const uploaderStatus = useAppSelector(selectAddPurchasesUploadStatus)
	const uploaderError = useAppSelector(selectAddPurchasesUploadError)
	const retrySafe = useAppSelector(selectAddPurchasesUploadRetrySafe)
	const uploadIsValid = useAppSelector(selectAddPurchasesUploadSuccessful)
	const accountingPeriodUpdateStatus = useAppSelector(selectAccountingPeriodsUpdateStatus)
	const accountingPeriodUpdateError = useAppSelector(selectAccountingPeriodsUpdateError)
	const [stageCompleted, setStageCompleted] = useState(false)
	const completedUploadPeriod = useRef<string>()
	const originalApi = useRef(api)
	const originalFile = useRef(fileToUpload)
	const accessChanged = useRef(false)
	const cancelUpload = useRef<() => void>(() => {})
	if (originalApi.current !== api) accessChanged.current = true

	const uploadedFilePeriodDetails = useAppSelector(selectAddPurchasesFileAccountingPeriod)
	const uploadedFileErrors = useAppSelector(selectAddPurchasesFileErrors)
	const uploadedFileRecords = useAppSelector(selectAddPurchasesFileRecords)
	const uploadedFileErrorCount = useAppSelector(selectAddPurchasesFileErrorCount)
	const uploadedFileAccountingPeriod = useAppSelector(selectAddPurchasesAccountingId)
	const completeStage = useCompleteAccountingStage(PurchaseUploadState.fileUploaded, () => setStageCompleted(true))
	const upload = useCleanup(() => {
		if (!fileToUpload || !fileToUploadAccountingPeriodId) {
			dispatch(ADDPURCHASESUPLOAD_FAILURE('Could not find the file or accounting period. Please choose the file again.'))
			return
		}
		const controller = new AbortController()
		let settled = false
		const cancel = () => {
			if (settled || controller.signal.aborted) return
			controller.abort()
			dispatch(ADDPURCHASESUPLOAD_UNCERTAIN('The upload was interrupted.'))
			if (!accessChanged.current) {
				notify({ body: 'The upload may have been imported. Check the accounting period before trying again.' })
			}
		}
		cancelUpload.current = cancel
		const run = async () => {
			setStageCompleted(false)
			dispatch(ACCOUNTINGPERIODS_UPDATE_RESET())
			const result = await dispatch(addPurchasesUploadFile(api, {
				file: fileToUpload,
				accountingPeriodId: fileToUploadAccountingPeriodId,
				filename: fileToUploadAccountingPeriodName ?? '-',
			}, { signal: controller.signal }))
			settled = true
			if (!controller.signal.aborted && result?.valid) {
				completedUploadPeriod.current = result.accountingPeriodId
				completeStage(result.accountingPeriodId)
			}
		}
		run()
		return cancel
	})

	useEffect(() => {
		if (accessChanged.current) {
			cancelUpload.current()
			if (originalFile.current) {
				URL.revokeObjectURL(originalFile.current)
				originalFile.current = undefined
			}
			dispatch(ADDPURCHASESUPLOAD_RESET())
			dispatch(ACCOUNTINGPERIODS_UPDATE_RESET())
			completedUploadPeriod.current = undefined
			setStageCompleted(false)
		}
		return () => cancelUpload.current()
	}, [api, dispatch])

	const accountingPeriodCrumbs = [
		{ label: 'Purchases', route: routes.companyPurchases.path },
		{ label: routes.companyAddAccountingPeriods.menuName as string, route: routes.companyAddAccountingPeriods.path },
		{ label: 'Accounting Period', route: generatePath(routes.companyViewAccountingPeriodDetails.path, { accountingPeriodIdVal: uploadedFileAccountingPeriod }) },
	]

	useEffect(() => {
		if (
			typeof fileToUpload === 'undefined' ||
			typeof fileToUploadAccountingPeriodId === 'undefined'
		) {
			// No file defined, we can't upload anything, send them back to pick a period and file
			navigate(routes.companyAddAccountingPeriods.path, { replace: true })
		} else if (uploaderStatus === PostStatus.initial) {
			// Let's start the upload process
			upload()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	if (accessChanged.current) {
		return (
			<PageTemplate testid='add-purchases-main'>
				<PageHeadings title='Upload CSV' />
				<InlineAlert type='error' className='mt-4'>
					Your access changed after the upload started. The upload may have been imported; check the accounting period before trying again.
				</InlineAlert>
				<Button type='Primary' text='Return to accounting periods' onClick={() => navigate(routes.companyAddAccountingPeriods.path)} />
			</PageTemplate>
		)
	}

	return (
		<PageTemplate testid='add-purchases-main'>
			<Helmet>
				<title>{routes.companyAddPurchasesUpload.name}</title>
			</Helmet>
			{uploaderStatus === PostStatus.sending && (
				<>
					<PageHeadings crumbs={[...accountingPeriodCrumbs, { label: 'Upload CSV' }]} />
					<LoadingSpinner loadingText='Uploading file...' />
				</>
			)}

			{uploaderStatus === PostStatus.error && (
				<>
					<PageHeadings crumbs={[...accountingPeriodCrumbs, { label: 'Upload CSV' }]} />

					<InlineAlert type='error' className='mb-5 mt-4'>						<p className='text-white'>
							There was an error sending your file. If this problem persists. please contact the
							Neoni team.
						</p>
						<p className='text-white mt-4'>{uploaderError}</p>
						{!retrySafe && <p className='text-white mt-4'>The upload may have been imported. Check the accounting period before trying again.</p>}
					</InlineAlert>

					<div>
						<Button
							onClick={() => {
								navigate(generatePath(routes.companyViewAccountingPeriodDetails.path, { accountingPeriodIdVal: uploadedFileAccountingPeriod ?? fileToUploadAccountingPeriodId}))
							}}
							type='Outlined'
							text='Cancel'
							additionalClass='mr-5'
						/>
						{retrySafe && <Button
							onClick={() => {
								if (
									typeof fileToUpload !== 'undefined' &&
									typeof fileToUploadAccountingPeriodId !== 'undefined'
								) {
									upload()
								}
							}}
							type='Primary'
							hasTrailingIcon={true}
							icon='fa-solid fa-upload'
							text='Retry upload with same file'
						/>}
					</div>
				</>
			)}

			{uploaderStatus === PostStatus.sent && (
				<>
					<PageHeadings crumbs={[...accountingPeriodCrumbs, { label: 'Upload CSV' }, { label: 'Check Data' }]} />

					{uploadIsValid ? (
						<>
							{/* Success */}

							<div className='rounded-lg bg-charcoal-600 p-4 mt-5 mb-2'>
								<p className='text-2xl text-white mb-5'>{stageCompleted ? 'Success!' : 'Import complete'}</p>
								<p className='text-white mb-7'>
									Your data has been imported. The next steps will be categorising your expenses to
									better estimate emissions.
								</p>

								{uploadedFilePeriodDetails && (
									<div className='p-4 py-2 rounded-lg bg-white'>
										<CsvResult
											startDate={uploadedFilePeriodDetails.start_date}
											endDate={uploadedFilePeriodDetails.end_date}
											errors={uploadedFileErrorCount}
											purchases={uploadedFileRecords}
										/>
									</div>
								)}
							</div>

							{accountingPeriodUpdateStatus === PostStatus.sending && (
								<LoadingSpinner loadingText='Updating the accounting period...' />
							)}
							{accountingPeriodUpdateStatus === PostStatus.error && (
								<InlineAlert type='error' className='mt-4'>
									<p className='text-white'>Your data was imported, but the accounting period could not be updated. Do not upload the file again.</p>
									<p className='text-white'>{accountingPeriodUpdateError}</p>
									{completedUploadPeriod.current ? (
										<Button type='Primary' text='Retry completion' onClick={() => completeStage(completedUploadPeriod.current)} />
									) : (
										<p className='text-white'>Please contact the Neoni team before continuing.</p>
									)}
								</InlineAlert>
							)}

							<div className='mt-4 mb-4'>
								<Button
									disabled={!stageCompleted}
									onClick={() => {
										navigate(
											generatePath(
												routes.companyRefineEmissionsGenerated.path,
												{ accountingPeriodId: uploadedFileAccountingPeriod},
											),
											{
												replace: true,
											}
										)
									}}
									type='Primary'
									hasTrailingIcon={true}
									icon='fa-solid fa-chevron-right'
									text='Refine data'
								/>
							</div>
						</>
					) : (
						<>
							{/* Fail */}

							<ContactMessage message='We found some errors in that file. Please fix any errors in your csv file and reupload.' />

							{uploadedFilePeriodDetails && (
								<div className='p-4 py-2 rounded-lg bg-charcoal-600 mb-5 mt-5'>
									<CsvResult
										headingTextClass='text-white'
										dataTextClass='text-charcoal-100'
										startDate={uploadedFilePeriodDetails.start_date}
										endDate={uploadedFilePeriodDetails.end_date}
										errors={uploadedFileErrorCount}
										purchases={uploadedFileRecords}
									/>
								</div>
							)}

							<div className='col-span-2 w-full'>
								<div className='flex flex-col rounded-lg shadow-lg bg-charcoal-600 h-full'>
									<div className='mt-8 flex flex-col'>
										<div className='-my-2 overflow-x-auto'>
											<div className='inline-block min-w-full py-2 align-middle'>
												<div className='overflow-hidden shadow-sm'>
													<table className='table-auto min-w-full divide-y divide-charcoal-300'>
														<tbody>
															{uploadedFileErrors?.map((error, index) => {
																return (
																	<tr
																		key={error.text + error.reference}
																		className='border-t-2 border-b-2 border-white'
																	>
																		<td className='p-lg text-left text-sm text-white leading-5'>
																			{index + 1}
																		</td>
																		<td className='p-lg text-left text-sm text-white leading-5'>
																			{error.reference}
																		</td>
																		<td className='p-lg text-left text-sm leading-5'>
																			<p className='text-charcoal-100'>Reason for error</p>
																			<p className='text-white'>{error.text}</p>
																		</td>
																	</tr>
																)
															})}
														</tbody>
													</table>
												</div>
											</div>
										</div>
									</div>
									<div className='bg-charcoal-700 mt-8 p-4 rounded'>
										<Button
											onClick={() => {
												navigate(
													generatePath(routes.companyViewAccountingPeriodDetails.path, {accountingPeriodIdVal: uploadedFileAccountingPeriod}),
													{ replace: true })
											}}
											type='Outlined'
											size='sm'
											hasLeadingIcon={true}
											icon='fa-solid fa-arrow-left'
											text='Back to accounting periods details'
											additionalClass='float-right'
										/>
									</div>
								</div>
							</div>
						</>
					)}
				</>
			)}
		</PageTemplate>
	)
}
export default AddPurchasesUpload
